import { memo, useCallback, useMemo, useState } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import EmptyStateMessage from '../../../shared/components/EmptyStateMessage';
import { EntityListPanel, type EntityListItemConfig } from '../../../shared/components/EntityListPanel';
import { ACCESS_ACTIONS, DOMAIN_LABELS, GOVERNANCE, appliedLevelLabel } from '../catalog';
import { useStoredToolAccess } from '../hooks/useToolConfigs';
import { roleGateOf } from '../utils/roleGate';
import type { GeneratedAccessAction, OperatorLevel } from '../types';
import ToolAccessPanel from './ToolAccessPanel';
import StoredReadFailed from './StoredReadFailed';

// The shipped operator level of an action, as the Actions list names it.
export const OPERATOR_LEVEL_LABEL: Record<OperatorLevel, string> = {
  L1: 'L1', L2: 'L2', admin: 'admin', 'admin-role': 'admin with the admin role', refused: 'no operator',
};

interface ActionAccessListProps {
  isOpen: boolean;
  editable: boolean;
}

/** Decision 9: who may call any action, set the same way as a tool's access. Kind marks stay with the tools. */
const ActionAccessList = memo<ActionAccessListProps>(({ isOpen, editable }) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { data: stored, isError: storedReadFailed } = useStoredToolAccess(isOpen);
  const selected = useMemo(() => ACCESS_ACTIONS.find((a) => a.actionId === selectedId) ?? null, [selectedId]);
  // Stable while the stored access is, so the list config below changes only when the access does. A failed read
  // shows no level, never the shipped one in its place (FE-14).
  const applied = useCallback((a: GeneratedAccessAction): string | null => (storedReadFailed ? null : appliedLevelLabel(
    OPERATOR_LEVEL_LABEL[a.requiredLevel], a.requiredLevel === 'admin-role', stored?.[a.actionId])),
  [stored, storedReadFailed]);

  // A new config re-renders every row of the list, so it is rebuilt only when the applied labels change.
  const config = useMemo<EntityListItemConfig<GeneratedAccessAction>>(() => ({
    getId: (a) => a.actionId,
    getPrimaryText: (a) => a.name,
    getSecondaryText: (a) => {
      const level = applied(a);
      const domain = DOMAIN_LABELS[a.domain] ?? a.domain;
      return level ? `${domain} · ${level}` : domain;
    },
    getSearchText: (a) => `${a.name} ${a.actionId} ${a.description}`,
    secondaryTypographyProps: { variant: 'caption' },
  }), [applied]);

  return (
    <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
      <Box sx={{ width: 320, borderRight: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
        <EntityListPanel
          entities={ACCESS_ACTIONS} selectedEntity={selected} isNew={false} saving={false} config={config}
          emptyStateText="No actions" emptyStateHelperText="" onSelect={(a) => setSelectedId(a.actionId)} searchable
          groupBy={(a) => [{ key: a.domain, label: DOMAIN_LABELS[a.domain] ?? a.domain }]}
        />
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {!selected ? (
          <EmptyStateMessage icon={LockIcon} title="Select an action" description="Pick an action to see and set who may call it." />
        ) : (
          <>
            <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>{selected.name}</Typography>
            <Chip label={`Cedar: ${selected.actionId}`} size="small" sx={{ fontFamily: 'monospace', mt: 0.5, mb: 1 }} />
            <Typography variant="body2" sx={{ mb: 2 }}>{selected.description}</Typography>
            <Box data-testid="applied-level" sx={{ mb: 2 }}>
              {storedReadFailed ? <StoredReadFailed /> : <Chip label={applied(selected)} size="small" />}
            </Box>
            <ToolAccessPanel
              actionId={selected.actionId} editable={editable} shippedLevel={OPERATOR_LEVEL_LABEL[selected.requiredLevel]}
              roleGated={Boolean(roleGateOf(GOVERNANCE[selected.actionId] ?? [])) || selected.requiredLevel === 'admin-role'}
            />
          </>
        )}
      </Box>
    </Box>
  );
});

ActionAccessList.displayName = 'ActionAccessList';

export default ActionAccessList;
